import { z } from "zod";
import { SocialPostType } from "../../types/models/campaign.models.types";
import {
  SocialCreatorType,
  SocialGender,
  SocialPlatforms,
} from "../../types/models/social.models.types";

function transformEmptyString(x: string) {
  return x.trim().length === 0 ? null : x;
}

export enum MilestoneRewardType {
  CASH = "cash",
  PRODUCT = "product",
  CASH_PRODUCT = "cash_product",
}

export const zCampaignFormBrand = z
  .object({
    name: z.string().min(1, "Name is required"),
    coverImage: z
      .object({
        id: z.string(),
        name: z.string(),
        url: z.string(),
      })
      .nullable()
      .default(null),
    coverImageFile: z
      .array(
        z.object({
          file: z.instanceof(File),
        })
      )
      .default([]),

    socialPlatform: z.nativeEnum(SocialPlatforms).nullable().default(null),
    socialPostType: z.nativeEnum(SocialPostType).nullable().default(null),
    socialCreatorType: z.nativeEnum(SocialCreatorType),
    genderPreference: z.nativeEnum(SocialGender).nullable().default(null),
    minimumFollowers: z.coerce.number().optional(),
    startDate: z
      .date()
      .refine(
        (date) => {
          if (!date) return true;
          return true;
        },
        { message: "Start date must be in the future" }
      )
      .nullable()
      .default(null),
    endDate: z.date().nullable().default(null),

    budget: z.coerce.number().optional(),
    milestoneRewardType: z
      .nativeEnum(MilestoneRewardType)
      .nullable()
      .default(null),
    milestones: z
      .array(
        z.object({
          count: z.coerce.number().min(0),
          payout: z.coerce.number().nullable(),
          product: z
            .object({
              image: z.object({
                id: z.string(),
                name: z.string(),
                url: z.string(),
              }),
              name: z.string().min(1),
              description: z.string().optional().nullable().default(null),
            })
            .optional()
            .nullable()
            .default(null),
          productFile: z
            .object({
              name: z.string(),
              file: z.instanceof(File),
            })
            .optional()
            .nullable()
            .default(null),
        })
      )

      // .min(1, "At least one milestone is required")
      .default([{ count: 0, payout: null }]),

    assets: z
      .array(
        z.object({
          id: z.string(),
          name: z.string(),
          url: z.string(),
        })
      )
      .default([]),
    assetFile: z
      .array(
        z.object({
          file: z.instanceof(File),
          note: z.string().nullable().default(null),
        })
      )
      .default([]),
    linkAssets: z
      .array(
        z.object({
          title: z.string().min(1, "Link title is required"),
          url: z.string().url("Invalid URL format"),
        })
      )
      .default([]),
    hashtags: z
      .string()
      .transform(transformEmptyString)
      .nullable()
      .default(null),
    mentions: z
      .string()
      .transform(transformEmptyString)
      .nullable()
      .default(null),
    aboutCampaign: z
      .string()
      .transform(transformEmptyString)
      .nullable()
      .default(null),
    do: z.string().transform(transformEmptyString).nullable().default(null),
    dont: z.string().transform(transformEmptyString).nullable().default(null),
    aboutPayment: z
      .string()
      .transform(transformEmptyString)
      .nullable()
      .default(null),
    aboutBrand: z
      .string()
      .transform(transformEmptyString)
      .nullable()
      .default(null),
    cpv: z
      .object({
        costPerView: z.coerce.number(),
        maxPayout: z.coerce.number(),
      })
      .optional()
      .nullable()
      .default(null),
  })
  .refine(
    (data) => {
      if (!data.startDate || !data.endDate) return true;
      return data.endDate > data.startDate;
    },
    {
      message: "End date must be after start date",
      path: ["endDate"],
    }
  );

export type IzCampaignFormBrand = z.infer<typeof zCampaignFormBrand>;
